import {
    Box,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    Input,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import BookingRequestContinueButton from "components/booking-request/booking-request-continue-button";
import { RichTextRenderer } from "components/rich-text-renderer";
import { SafeHTML } from "components/safe-html";
import { useBookingActionsContext } from "hooks/use-booking-state";
import { BookingFeature, PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useStepTracking } from "logger/use-step-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { resolveRichTextField } from "types/rich-text-field";

interface GuestFormData {
    guests: number;
    children?: number;
}

export function SelectRequestGuests() {
    const { t, i18n } = useTranslation();
    const { setMultipleStateProps } = useBookingActionsContext();
    const { data: settings } = useExternalBookingSettings();
    const inquiryInfoTemplate = settings?.messageTemplates?.inquiryInfo;
    const {
        activateWebChildren,
        childrenAge,
        maxWebGuests: maxWebGuestsSetting,
    } = settings?.unitsMetaData ?? {};

    // OLD SYSTEM: Keep for backward compatibility
    useStepTracking({
        stepName: TrackingStepName.RequestGuestStep,
        metadata: {
            maxWebGuests: maxWebGuestsSetting ?? 0,
        },
    });

    // NEW SYSTEM: Enhanced tracking with session context
    usePageTracking({
        pageName: "BookingRequestGuests",
        pageType: PageType.WizardStep,
        metadata: {
            feature: BookingFeature.Request,
            maxWebGuests: maxWebGuestsSetting ?? 0,
            activateWebChildren: activateWebChildren ?? false,
        },
    });

    const maxWebGuests = maxWebGuestsSetting ?? 0;

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid, touchedFields },
    } = useForm<GuestFormData>({
        mode: "onTouched",
        defaultValues: {
            guests: undefined,
            children: undefined,
        },
    });
    const onSubmit = (data: GuestFormData) => {
        const guests = data.children ? data.guests + data.children : data.guests;
        setMultipleStateProps?.({
            guests: guests,
            guestsChildren: activateWebChildren ? data.children || 0 : undefined,
        });
    };

    const submitIfValid = () => {
        if (!isValid || isBelowCombinedMinimum) return;
        void handleSubmit(onSubmit)();
    };

    const handleContinue = () => {
        submitIfValid();
    };

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        submitIfValid();
    };

    const minGuests = maxWebGuests + 1;
    const childrenAgeDescription = childrenAge
        ? t("experienceView.childrenAged", { age: childrenAge })
        : undefined;
    const footerHeight = "104px";

    const watchedGuests = watch("guests");
    const watchedChildren = watch("children");
    const hasGuestsValue = typeof watchedGuests === "number" && !Number.isNaN(watchedGuests);
    const combinedTotal = hasGuestsValue ? watchedGuests + (watchedChildren || 0) : 0;
    const isBelowCombinedMinimum =
        activateWebChildren && hasGuestsValue && combinedTotal < minGuests;
    const showCombinedMinimumError =
        isBelowCombinedMinimum && (touchedFields.guests || touchedFields.children);

    return (
        <form onSubmit={handleFormSubmit}>
            <FormControl>
                {(inquiryInfoTemplate || settings?.inquiryInfo) && (
                    <Stack spacing={ThemeSpaceVariable.Medium}>
                        <Heading as="h2" fontSize={ThemeFontSizeVariable.X2Large}>
                            {t("bookingRequest.title")}
                        </Heading>
                        {inquiryInfoTemplate ? (
                            // Box rather than Text: Text renders a <p>, and RichTextRenderer emits
                            // block-level elements, so nesting them puts a <div> inside a <p>. Same
                            // container the other rich-text call sites use.
                            <Box wordBreak="break-word" overflowWrap="anywhere">
                                <RichTextRenderer
                                    content={resolveRichTextField(
                                        inquiryInfoTemplate,
                                        i18n.language
                                    )}
                                />
                            </Box>
                        ) : (
                            <Text wordBreak="break-word" overflowWrap="anywhere">
                                <SafeHTML html={settings?.inquiryInfo} />
                            </Text>
                        )}
                    </Stack>
                )}
                <Stack
                    spacing={ThemeSpaceVariable.Medium}
                    mb={{ base: footerHeight, md: ThemeSpaceVariable.Large }}
                    mt={ThemeSpaceVariable.Medium}
                >
                    <FormControl isInvalid={!!errors.guests}>
                        <FormLabel mb={ThemeSpaceVariable.XSmall}>
                            {activateWebChildren ? t("adults") : t("bookingRequest.guests.label")}
                            <Box
                                as="span"
                                color={ThemeColorVariable.Error}
                                ml={ThemeSpaceVariable.XSmall}
                            >
                                *
                            </Box>
                        </FormLabel>
                        <Input
                            type="number"
                            min="1"
                            size="lg"
                            {...register("guests", {
                                required: {
                                    value: true,
                                    message: t("bookingRequest.validation.guestsRequired"),
                                },
                                min: !activateWebChildren
                                    ? {
                                          value: minGuests,
                                          message: t("bookingRequest.validation.guestsMinimum", {
                                              min: minGuests,
                                          }),
                                      }
                                    : undefined,
                                valueAsNumber: true,
                            })}
                        />
                        <FormErrorMessage>{errors.guests?.message}</FormErrorMessage>
                    </FormControl>
                    {activateWebChildren && (
                        <FormControl isInvalid={!!errors.children}>
                            <FormLabel mb={ThemeSpaceVariable.XSmall}>
                                {childrenAgeDescription ? childrenAgeDescription : t("children")}
                            </FormLabel>
                            <Input
                                type="number"
                                min="0"
                                size="lg"
                                {...register("children", {
                                    min: {
                                        value: 0,
                                        message: t("bookingRequest.validation.childrenMinimum"),
                                    },
                                    valueAsNumber: true,
                                })}
                            />
                            <FormErrorMessage>{errors.children?.message}</FormErrorMessage>
                        </FormControl>
                    )}
                    <FormControl isInvalid={showCombinedMinimumError}>
                        <FormErrorMessage mt={0}>
                            {t("bookingRequest.validation.guestsMinimumWithChildren", {
                                min: minGuests,
                            })}
                        </FormErrorMessage>
                    </FormControl>
                </Stack>
                <BookingRequestContinueButton
                    handleContinue={handleContinue}
                    disabled={!isValid || isBelowCombinedMinimum}
                />
            </FormControl>
        </form>
    );
}
